import { Inject, Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import type { IBriefingPage, IReferenceCompanyBriefing } from '@/shared/model/briefing-widget.model';
import { onErrorImageAvatar } from '@/util/common-util';
import BriefingService from '@/service/briefing.service';
import { PAGES } from '@/shared/constants/ds-constants';
import reference from '@/entities/insights/selected-report/reference/reference.component';
import AskConversation from '@/v2/home/ask-goover/ask-conversation/ask-conversation.vue';

const briefingStore = namespace('briefingStore');

@Component({
  computed: {
    reference() {
      return reference;
    },
    PAGES() {
      return PAGES;
    },
  },
  methods: { onErrorImageAvatar },
  components: {
    askGoover: AskConversation,
  },
})
export default class CompanyWidgetViewDetail extends Vue {
  @Prop(Object) companyBp: IBriefingPage | undefined;

  @briefingStore.Getter
  public getCompanyWidgetDetail!: any;

  @briefingStore.Getter
  public getCompanyBpDetail!: IBriefingPage; // Accessing companyBpDetail from the store

  @briefingStore.Mutation
  public setCompanyWidgetDetail: (payload: any) => void;

  @Inject('briefingService')
  private briefingService: () => BriefingService;

  @briefingStore.Getter
  public getFeaturedPages!: IBriefingPage[];

  @briefingStore.Getter
  public getSocialPages!: IBriefingPage[];

  @briefingStore.Mutation
  public setSelectedSocialPage: (payload: any) => void;

  @briefingStore.Mutation
  public setFeaturedPages: (payload: IBriefingPage[]) => void;

  @briefingStore.Mutation
  public setSocialPages: (payload: IBriefingPage[]) => void;

  @briefingStore.Mutation
  public setIsCloseCompanyBpDetail!: (payload: boolean) => void;

  @briefingStore.Mutation
  public setIsSubscribedBriefing: (payload: boolean) => void;

  @briefingStore.Mutation
  public setTotalSubscribeBriefing: (payload: number) => void;

  @briefingStore.Getter
  public getTotalSubscribeBriefing: number;

  detailData = {
    companyName: '',
    title: '',
    author: '',
    publishDate: '',
    viewCount: '',
    content: '',
    references: [] as IReferenceCompanyBriefing[],
    briefingInfo: {
      name: '',
      subcribeCount: 0,
      subcribeStatus: 'UN_SUBSCRIBE',
    },
    userProfile: {
      lastName: '',
      avatarImageUrl: '',
      userKey: '',
    },
  };

  i18nBriefingNamePath = '';
  isLoadingSubcribe = false;
  getIsShowPopUpDetail = true;
  subscribeStatus = '';
  stockCode = '';
  companyBpData: IBriefingPage = null;

  destroyed() {
    this.$store.commit('briefingStore/setCompanyWidgetDetail', { name: '', widgetData: {} });
  }

  deactivated() {
    this.$store.commit('briefingStore/setCompanyWidgetDetail', { name: '', widgetData: {} });
  }

  created() {
    this.companyBpData = this.companyBp === undefined ? this.getCompanyBpDetail : this.companyBp;
    this.detailData.companyName = this.companyBpData?.briefingPageName || '';
    this.detailData.briefingInfo.name = this.companyBpData?.briefingPageName || '';
    this.detailData.briefingInfo.subcribeCount = this.companyBpData?.subscribeCount || 0;
    this.detailData.briefingInfo.subcribeStatus = this.companyBpData?.subscribeStatus || 'UN_SUBSCRIBE';

    this.subscribeStatus = this.companyBpData?.subscribeStatus;
    this.stockCode = this.companyBpData?.stockCode;

    this.initDetailData();
  }

  @Watch('$route')
  onRouteChanged() {
    this.closeDetail();
  }

  initDetailData() {
    const tempUserProfile = this.getCompanyWidgetDetail?.widgetData?.userProfile || this.companyBpData?.userProfile || null;
    let tempReference;

    if (tempUserProfile) {
      this.detailData.userProfile.avatarImageUrl = tempUserProfile.imageUrl || '';
      this.detailData.userProfile.lastName = tempUserProfile.lastName || '';
      this.detailData.userProfile.userKey = tempUserProfile.userKey || '';
    }

    switch (this.getCompanyWidgetDetail.name) {
      case 'Analyst Detail':
        this.detailData.title = this.getCompanyWidgetDetail?.widgetData?.expertReport.title || '';
        this.detailData.content = this.getCompanyWidgetDetail?.widgetData?.expertReport.summaryContent || '';
        this.detailData.publishDate =
          (this.getCompanyWidgetDetail?.widgetData?.expertReport.reportDate &&
            this.modifyDateData(this.getCompanyWidgetDetail?.widgetData?.expertReport.reportDate)) ||
          '';
        this.detailData.briefingInfo.name =
          this.getCompanyWidgetDetail?.widgetData?.companyBpName || this.companyBpData?.briefingPageName || '';
        // this.detailData.viewCount = this.getCompanyWidgetDetail?.widgetData?.expertReport.title || '';
        tempReference = this.makeReference('AnalystDetail');
        this.detailData.references.push(tempReference);
        break;

      case 'PublicNotificationSummaryDetail':
        if (this.getCompanyWidgetDetail?.widgetData?.notificationSummaryDataDetail) {
          const ntfSummaryDetail = this.getCompanyWidgetDetail?.widgetData?.ntfSummaryDetail;
          this.detailData.title = ntfSummaryDetail.title || '';
          this.detailData.content = ntfSummaryDetail.summaryContent || '';
          this.detailData.publishDate = ntfSummaryDetail.publishedDate || '';
          if (this.detailData.publishDate) {
            this.detailData.publishDate = this.modifyDateData(this.detailData.publishDate) || '';
          }
          this.detailData.briefingInfo.name =
            this.getCompanyWidgetDetail?.widgetData?.companyBpName || this.companyBpData?.briefingPageName || '';
          this.i18nBriefingNamePath = 'publicNotificationSummaryDetail.detailName';
          tempReference = this.makeReference('PublicNotificationSummaryDetail', ntfSummaryDetail);
          this.detailData.references.push(tempReference);
        }

        break;

      case 'BuzzBuzzSummaryDetail':
        if (this.getCompanyWidgetDetail.widgetData.buzzBuzzSummaryDataDetail) {
          const buzzBuzzDetail = this.getCompanyWidgetDetail?.widgetData?.buzzBuzzDetail;
          this.detailData.title = buzzBuzzDetail.title || '';
          this.detailData.content = buzzBuzzDetail.summaryContent || '';
          this.detailData.publishDate = buzzBuzzDetail.summaryRegistDate || '';
          if (this.detailData.publishDate) {
            this.detailData.publishDate = this.modifyDateData(this.detailData.publishDate) || '';
          }
          this.detailData.briefingInfo.name =
            this.getCompanyWidgetDetail?.widgetData?.companyBpName || this.companyBpData?.briefingPageName || '';
          this.i18nBriefingNamePath = 'buzzBuzzSummaryDetail.detailName';
          if (buzzBuzzDetail.stockChatOriginal) {
            const stockChatOriginal = buzzBuzzDetail.stockChatOriginal;
            stockChatOriginal.forEach((item: any) => {
              tempReference = this.makeReference('BuzzBuzzSummaryDetail', item);
              this.detailData.references.push(tempReference);
            });
          }
        }

        break;

      default:
        break;
    }
  }

  closeDetail() {
    this.setIsCloseCompanyBpDetail(true);
    this.$store.commit('briefingStore/setCompanyWidgetDetail', {});
    this.$store.commit('briefingStore/setCompanyBpDetail', {}); /*Reset the store -> prevent passing data to another card*/
  }

  handleClickBriefingName() {
    this.setCompanyWidgetDetail({});
  }

  onSelectUserSocial(agentKey?: string) {
    const tempUserKey = this.$store.getters?.userIdentity?.id || JSON.parse(localStorage.getItem('localStorageUser'))?.id || '';

    if (tempUserKey && agentKey) {
      this.setCompanyWidgetDetail({});

      if (tempUserKey === agentKey) {
        this.$router.push('/my-briefing');
      } else {
        this.$router.push(`/social-user/${agentKey}`);
      }
    }
  }

  makeReference(widgetDetailName: string, originalData?: any) {
    const tempReference = {} as IReferenceCompanyBriefing;

    switch (widgetDetailName) {
      case 'PublicNotificationSummaryDetail':
        tempReference.title = originalData?.title || '';
        tempReference.description = originalData?.summaryContent || '';
        tempReference.url = originalData?.url || '';
        tempReference.publisher = originalData?.publisher || '';
        tempReference.publishedDate = originalData?.registDate ? this.modifyDateData(originalData?.registDate) : '';
        tempReference.faviconImage = this.getFaviconUrl(originalData?.url || '') || '';

        break;

      case 'BuzzBuzzSummaryDetail':
        tempReference.title = originalData?.title || '';
        tempReference.description = originalData?.content || '';
        tempReference.url = originalData?.url || '';
        tempReference.publisher = originalData?.publisher || '';
        tempReference.publishedDate = this.modifyDateData(originalData?.registDate ? originalData?.registDate : '') || '';
        tempReference.faviconImage = this.getFaviconUrl(originalData?.url || '') || '';
        break;

      case 'AnalystDetail':
        tempReference.title = this.getCompanyWidgetDetail?.widgetData?.expertReport.title || '';
        tempReference.description = this.getCompanyWidgetDetail?.widgetData?.expertReport.summaryContent || '';
        tempReference.publishedDate =
          (this.getCompanyWidgetDetail?.widgetData?.expertReport.reportDate &&
            this.modifyDateData(this.getCompanyWidgetDetail?.widgetData?.expertReport.reportDate)) ||
          '';
        tempReference.publisher = this.getCompanyWidgetDetail?.widgetData?.expertReport.publisher || '';
        tempReference.url = this.getCompanyWidgetDetail?.widgetData?.expertReport?.url || '';
        tempReference.faviconImage = this.getFaviconUrl(this.getCompanyWidgetDetail?.widgetData?.expertReport.url || '') || '';
        break;

      default:
        break;
    }

    return tempReference;
  }

  getFaviconUrl(websiteUrl: string): string {
    try {
      const url = new URL(websiteUrl);
      return `${url.origin}/favicon.ico`;
    } catch (e) {
      return '';
    }
  }

  handleOpenReferenceUrl(url: string): void {
    if (url && url.trim().length > 0) {
      window.open(url, '_blank');
    }
  }

  onErrorFaviconReference(event) {
    if (event) {
      event.target.src = 'content/images/images_v3/sample/@sample-media-1.png';
    }
  }

  toggleSubscribed() {
    if (this.companyBpData) {
      this.isLoadingSubcribe = true;
      const toggleStatus = this.companyBpData?.subscribeStatus === 'SUBSCRIBE' ? 'UN_SUBSCRIBE' : 'SUBSCRIBE';
      const subscribeCount = this.companyBpData?.subscribeCount + (this.companyBpData?.subscribeStatus === 'SUBSCRIBE' ? -1 : 1);
      this.briefingService()
        .updateSubscribeBriefingPage(this.companyBpData?.bpId, toggleStatus)
        .then(response => {
          /* update view */
          Vue.set(this.companyBpData, 'subscribeStatus', toggleStatus);
          Vue.set(this.companyBpData, 'subscribeCount', subscribeCount);
          this.detailData.briefingInfo.subcribeCount = subscribeCount;
          this.detailData.briefingInfo.subcribeStatus = toggleStatus;
          /* update in store */
          switch (toggleStatus) {
            case 'SUBSCRIBE': {
              const featuredStore = this.getFeaturedPages.findIndex(e => e.bpId === this.companyBpData.bpId);
              if (featuredStore >= 0) {
                this.getFeaturedPages.splice(featuredStore, 1);
              }
              this.getSocialPages.unshift({
                ...this.companyBpData,
                subscribeStatus: toggleStatus,
                subscribeCount: subscribeCount,
              });
              this.subscribeStatus = 'SUBSCRIBE';
              this.setTotalSubscribeBriefing(++this.getTotalSubscribeBriefing);
              break;
            }
            case 'UN_SUBSCRIBE': {
              const indexStore = this.getSocialPages.findIndex(e => e.bpId === this.companyBpData.bpId);
              if (indexStore >= 0) {
                this.getSocialPages.splice(indexStore, 1);
              }

              this.setSelectedSocialPage({});
              this.subscribeStatus = 'UN_SUBSCRIBE';
              this.setIsSubscribedBriefing(false);
              break;
            }
          }

          this.setFeaturedPages(this.getFeaturedPages);
          this.setSocialPages(this.getSocialPages);
        })
        .finally(() => {
          this.isLoadingSubcribe = false;
        });
    }
  }

  modifyDateData(dateInput?: any) {
    if (dateInput) {
      try {
        dateInput = dateInput.replace('T', ' ');
        if (dateInput && dateInput.length > 16) {
          dateInput = dateInput.slice(0, 16);
        }
        return dateInput;
      } catch (e) {
        return dateInput;
      }
    }
  }
}
